<template>
  <SlideshowPanel
    class="slideshow-settings"
    icon="present"
    :lead="texts.settingsLead"
    :title="forFilm('settingsTitle', 'lifeSettingsTitle')"
  >
    <div class="slideshow-settings__switches">
      <SlideshowSwitch v-model="qr" :label="texts.settingsQr" />
      <SlideshowSwitch v-model="loop" :label="texts.settingsLoop" />
      <SlideshowSwitch
        sub
        v-model="bookends"
        :disabled="!loop"
        :label="texts.settingsBookends"
      />
    </div>

    <div
      class="slideshow-settings__lengths"
      role="radiogroup"
      v-if="lengths"
      :aria-label="texts.settingsLength"
    >
      <label
        class="slideshow-settings__length"
        v-for="choice in choices"
        :key="String(choice.long)"
        :class="{ 'is-chosen': long === choice.long }"
      >
        <input
          class="slideshow-settings__radio"
          type="radio"
          v-model="long"
          :name="groupName"
          :value="choice.long"
        >
        <b>{{ choice.label }}</b>
        <span>{{ minutes(choice.seconds) }}</span>
      </label>
    </div>
  </SlideshowPanel>
</template>

<script lang="ts" setup>
  import { computed, useId } from "vue";
  import {
    forFilm,
    slideshowTexts,
    viewer,
  } from "../../composables/useSlideshow";
  import { sprintf } from "../../slideshow/playlist";
  import SlideshowPanel from "./SlideshowPanel.vue";
  import SlideshowSwitch from "./SlideshowSwitch.vue";

  /**
   * The viewer's settings, for showing the film at a memorial gathering. Each one changes
   * the film as it plays, so there's nothing to start or save.
   */
  const props = defineProps<{
    /** The two films' lengths in seconds; null when the page has no more than the selection. */
    lengths: { short: number; long: number } | null;
  }>();
  const loop = defineModel<boolean>("loop", { required: true });
  const qr = defineModel<boolean>("qr", { required: true });
  const bookends = defineModel<boolean>("bookends", { required: true });
  const long = defineModel<boolean>("long", { required: true });

  const texts = slideshowTexts();
  const groupName = `slideshow-length-${useId()}`;

  // `long` is the length that isn't the film's own: for the life story, the selection.
  const choices = computed(() => {
    if (!props.lengths) {
      return [];
    }
    const short = {
      label: texts.settingsShort,
      long: false,
      seconds: props.lengths.short,
    };
    const whole = {
      label: texts.settingsLong,
      long: true,
      seconds: props.lengths.long,
    };
    return viewer.kind === "life"
      ? [
          { label: texts.lengthWhole, long: false, seconds: whole.seconds },
          { label: texts.lengthSelection, long: true, seconds: short.seconds },
        ]
      : [short, whole];
  });

  // Only the length: the film is photos, films and greetings, so a count of "bilder" misleads.
  function minutes(seconds: number) {
    return sprintf(texts.settingsMeta, Math.max(1, Math.round(seconds / 60)));
  }
</script>
